import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { RefreshCw, AlertCircle, ArrowLeft, ChevronDown, MessageSquare, Star, ThumbsUp, ThumbsDown, Filter, Search, ChevronUp, Calendar, User, Globe } from 'lucide-react';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

// Types
interface LogEntry {
  id: number;
  request_id: number;
  text: string;
  output_text: string;
  cost: string;
  created_at: string;
  metadata: string;
  chat_history?: string;
}

interface MetadataType {
  headers: {
    country?: string;
    city?: string;
    timezone?: string;
    deviceBrand?: string;
    deviceModel?: string;
    platform?: string;
    androidVersion?: string;
    appVersion?: string;
    sdkVersion?: string;
    systemName?: string;
    appBuild?: string;
    architecture?: string;
    deviceId?: string;
    source?: string;
    mode?: string;
    paidUser?: string;
    realIp?: string;
  };
  cf: Record<string, any>;
}

interface FeedbackAnalysis {
  id: number;
  originalText: string;
  translatedText?: string;
  sentiment: 'positive' | 'negative' | 'neutral';
  category: 'bug' | 'feature_request' | 'praise' | 'complaint' | 'question' | 'other';
  confidence: number;
  themes: string[];
  language: string;
  priority: 'low' | 'medium' | 'high';
  country?: string;
  isPaidUser: boolean;
  createdAt: string;
  cost: number;
  isRead?: boolean;
  notes?: string;
}

interface FeedbackFilters {
  priority: 'all' | 'high' | 'medium' | 'low';
  sentiment: 'all' | 'positive' | 'negative' | 'neutral';
  category: 'all' | 'bug' | 'feature_request' | 'praise' | 'complaint' | 'question' | 'other';
  userType: 'all' | 'paid' | 'free';
  search: string;
}

interface ChartCardProps {
  title: string;
  id: string;
  children: React.ReactNode;
  className?: string;
  filterComponent?: React.ReactNode;
}

const FeedbackDashboard: React.FC = () => {
  const [analyzedFeedback, setAnalyzedFeedback] = useState<FeedbackAnalysis[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState(new Date());
  const [expandedCards, setExpandedCards] = useState<Set<string>>(new Set(['sentiment', 'categories', 'geography', 'feedback-list']));
  const [isMobile, setIsMobile] = useState(false);
  
  // Feedback list state
  const [filters, setFilters] = useState<FeedbackFilters>({
    priority: 'all',
    sentiment: 'all',
    category: 'all',
    userType: 'all',
    search: ''
  });
  const [expandedFeedback, setExpandedFeedback] = useState<Set<number>>(new Set());
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Reset current page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [filters.priority, filters.sentiment, filters.category, filters.userType, filters.search]);


  // AI Analysis Functions
  const detectLanguage = (text: string): string => {
    // Simple language detection based on common patterns
    const patterns = {
      'sv': /\b(är|och|det|som|för|på|med|av|att|till|från|han|hon|de|vi|jag|du)\b/gi,
      'en': /\b(the|and|is|was|are|were|to|from|with|that|this|have|has|will|would)\b/gi,
      'de': /\b(der|die|das|und|ist|war|sind|waren|zu|von|mit|dass|diese|haben|wird)\b/gi,
      'es': /\b(el|la|los|las|y|es|fue|son|fueron|de|con|que|este|esta|tienen|será)\b/gi,
      'fr': /\b(le|la|les|et|est|était|sont|étaient|de|avec|que|cette|ont|sera)\b/gi
    };

    let maxMatches = 0;
    let detectedLang = 'en';

    for (const [lang, pattern] of Object.entries(patterns)) {
      const matches = text.match(pattern);
      const matchCount = matches ? matches.length : 0;
      if (matchCount > maxMatches) {
        maxMatches = matchCount;
        detectedLang = lang;
      }
    }

    return detectedLang;
  };

  const analyzeSentiment = (text: string): { sentiment: 'positive' | 'negative' | 'neutral', confidence: number } => {
    const positiveWords = ['good', 'great', 'excellent', 'amazing', 'love', 'perfect', 'awesome', 'fantastic', 'wonderful', 'brilliant', 'helpful', 'useful', 'easy', 'fast', 'quick', 'smooth', 'nice', 'thank', 'thanks', 'appreciate', 'impressed', 'outstanding', 'superb'];
    const negativeWords = ['bad', 'terrible', 'awful', 'hate', 'horrible', 'worst', 'sucks', 'broken', 'bug', 'issue', 'problem', 'error', 'fail', 'failing', 'crash', 'crashes', 'slow', 'difficult', 'hard', 'confusing', 'frustrating', 'disappointed', 'annoying', 'useless'];
    
    const words = text.toLowerCase().split(/\s+/);
    let positiveScore = 0;
    let negativeScore = 0;

    words.forEach(word => {
      if (positiveWords.includes(word)) positiveScore++;
      if (negativeWords.includes(word)) negativeScore++;
    });

    const totalScore = positiveScore + negativeScore;
    if (totalScore === 0) {
      return { sentiment: 'neutral', confidence: 0.5 };
    }

    const confidence = Math.min(totalScore / words.length * 10, 1);
    
    if (positiveScore > negativeScore) {
      return { sentiment: 'positive', confidence };
    } else if (negativeScore > positiveScore) {
      return { sentiment: 'negative', confidence };
    } else {
      return { sentiment: 'neutral', confidence };
    }
  };

  const categorizeText = (text: string): { category: FeedbackAnalysis['category'], themes: string[] } => {
    const categories = {
      bug: ['bug', 'error', 'crash', 'issue', 'problem', 'broken', 'not working', 'fails', 'glitch'],
      feature_request: ['request', 'add', 'feature', 'suggestion', 'improve', 'enhancement', 'wish', 'want', 'need', 'could you'],
      praise: ['great', 'love', 'excellent', 'amazing', 'thank', 'awesome', 'perfect', 'wonderful'],
      complaint: ['disappointed', 'frustrated', 'annoying', 'terrible', 'awful', 'hate', 'worst'],
      question: ['how', 'what', 'why', 'when', 'where', 'can i', 'help', '?']
    };

    const lowerText = text.toLowerCase();
    let maxScore = 0;
    let detectedCategory: FeedbackAnalysis['category'] = 'other';
    const themes: string[] = [];

    for (const [category, keywords] of Object.entries(categories)) {
      let score = 0;
      const categoryThemes: string[] = [];
      
      keywords.forEach(keyword => {
        if (lowerText.includes(keyword)) {
          score++;
          categoryThemes.push(keyword);
        }
      });

      if (score > maxScore) {
        maxScore = score;
        detectedCategory = category as FeedbackAnalysis['category'];
        themes.splice(0, themes.length, ...categoryThemes);
      }
    }

    return { category: detectedCategory, themes };
  };

  const calculatePriority = (sentiment: string, category: string, isPaidUser: boolean): FeedbackAnalysis['priority'] => {
    let score = 0;
    
    // Negative sentiment increases priority
    if (sentiment === 'negative') score += 2;
    else if (sentiment === 'neutral') score += 1;
    
    // Bug reports and complaints are higher priority
    if (category === 'bug' || category === 'complaint') score += 2;
    else if (category === 'feature_request') score += 1;
    
    // Paid users get higher priority
    if (isPaidUser) score += 1;
    
    if (score >= 4) return 'high';
    if (score >= 2) return 'medium';
    return 'low';
  };

  const analyzeFeedback = useCallback((logs: LogEntry[]): FeedbackAnalysis[] => {
    return logs.map(log => {
      let metadata: MetadataType = { headers: {}, cf: {} };
      try {
        if (log.metadata) {
          metadata = JSON.parse(log.metadata) as MetadataType;
        }
      } catch (e) {
        console.error('Failed to parse metadata:', e);
      }

      const feedbackText = `${log.text} ${log.output_text}`;
      const sentimentResult = analyzeSentiment(feedbackText);
      const categoryResult = categorizeText(feedbackText);
      const language = detectLanguage(feedbackText);
      const isPaidUser = metadata.headers.paidUser === 'true';
      const priority = calculatePriority(sentimentResult.sentiment, categoryResult.category, isPaidUser);

      return {
        id: log.id,
        originalText: feedbackText,
        sentiment: sentimentResult.sentiment,
        category: categoryResult.category,
        confidence: sentimentResult.confidence,
        themes: categoryResult.themes,
        language,
        priority,
        country: metadata.headers.country || 'Unknown',
        isPaidUser,
        createdAt: log.created_at,
        cost: parseFloat(log.cost || '0')
      };
    });
  }, []);

  // Simple filtering logic
  const filteredFeedback = useMemo(() => {
    let result = analyzedFeedback;

    // Filter by priority
    if (filters.priority !== 'all') {
      result = result.filter(item => item.priority === filters.priority);
    }

    // Filter by sentiment
    if (filters.sentiment !== 'all') {
      result = result.filter(item => item.sentiment === filters.sentiment);
    }

    // Filter by category
    if (filters.category !== 'all') {
      result = result.filter(item => item.category === filters.category);
    }

    // Filter by user type
    if (filters.userType !== 'all') {
      const isPaid = filters.userType === 'paid';
      result = result.filter(item => item.isPaidUser === isPaid);
    }

    // Filter by search text
    if (filters.search.trim()) {
      const searchTerm = filters.search.toLowerCase().trim();
      result = result.filter(item => 
        item.originalText.toLowerCase().includes(searchTerm)
      );
    }

    // Sort by priority: high -> medium -> low
    const priorityOrder = { high: 3, medium: 2, low: 1 };
    result.sort((a, b) => {
      const priorityDiff = priorityOrder[b.priority] - priorityOrder[a.priority];
      if (priorityDiff !== 0) return priorityDiff;
      
      // Secondary sort by creation date (newest first)
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    });

    return result;
  }, [analyzedFeedback, filters]);

  const toggleFeedbackExpand = useCallback((id: number) => {
    setExpandedFeedback(prev => {
      const newExpanded = new Set(prev);
      if (newExpanded.has(id)) {
        newExpanded.delete(id);
      } else {
        newExpanded.add(id);
      }
      return newExpanded;
    });
  }, []);

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high': return 'bg-red-500/20 text-red-400 border-red-500/30';
      case 'medium': return 'bg-orange-500/20 text-orange-400 border-orange-500/30';
      case 'low': return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
      default: return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
    }
  };

  const getSentimentIcon = (sentiment: string) => {
    switch (sentiment) {
      case 'positive': return <ThumbsUp className="w-4 h-4 text-green-400" />;
      case 'negative': return <ThumbsDown className="w-4 h-4 text-red-400" />;
      case 'neutral': return <MessageSquare className="w-4 h-4 text-gray-400" />;
      default: return <MessageSquare className="w-4 h-4 text-gray-400" />;
    }
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'bug': return '🐛';
      case 'feature_request': return '✨';
      case 'praise': return '👏';
      case 'complaint': return '😞';
      case 'question': return '❓';
      default: return '💬';
    }
  };

  const truncateText = (text: string, maxLength: number = 150) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  // Memoize the filter component to prevent re-mounting
  const desktopFilterComponent = useMemo(() => (
    <div className={`${isMobile ? 'hidden' : 'flex items-center space-x-2'}`}>
      <Filter className="w-4 h-4" />
      <select 
        value={filters.priority} 
        onChange={(e) => {
          e.stopPropagation();
          setFilters({...filters, priority: e.target.value as any});
        }}
        className="bg-gray-700 text-white text-xs rounded px-2 py-1"
      >
        <option value="all">All Priorities</option>
        <option value="high">High Priority</option>
        <option value="medium">Medium Priority</option>
        <option value="low">Low Priority</option>
      </select>
      <select 
        value={filters.sentiment} 
        onChange={(e) => {
          e.stopPropagation();
          setFilters({...filters, sentiment: e.target.value as any});
        }}
        className="bg-gray-700 text-white text-xs rounded px-2 py-1"
      >
        <option value="all">All Sentiments</option>
        <option value="positive">Positive</option>
        <option value="negative">Negative</option>
        <option value="neutral">Neutral</option>
      </select>
      <select 
        value={filters.category} 
        onChange={(e) => {
          e.stopPropagation();
          setFilters({...filters, category: e.target.value as any});
        }}
        className="bg-gray-700 text-white text-xs rounded px-2 py-1"
      >
        <option value="all">All Categories</option>
        <option value="bug">Bugs</option>
        <option value="feature_request">Feature Requests</option>
        <option value="praise">Praise</option>
        <option value="complaint">Complaints</option>
        <option value="question">Questions</option>
        <option value="other">Other</option>
      </select>
    </div>
  ), [isMobile, filters]);

  // Simple search input handler
  const handleSearchInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setFilters({...filters, search: e.target.value});
  }, [filters]);

  useEffect(() => {
    const fetchFeedbackData = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams({
          mode: 'feedback',
          limit: '1000',
          // Add time period filtering if needed
        });

        const response = await fetch(`https://api.notely.se/dashboard/process-logs?${params}`);
        if (!response.ok) throw new Error('Failed to fetch feedback data');
        const data = await response.json();
        
        const analyzed = analyzeFeedback(data.logs || []);
        setAnalyzedFeedback(analyzed);
        setLastUpdated(new Date());
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'An error occurred';
        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    };

    fetchFeedbackData();
  }, [analyzeFeedback]);

  const ChartCard = ({ title, id, children, className = '', filterComponent }: ChartCardProps) => {
    const isExpanded = expandedCards.has(id);

    const toggleCard = () => {
      const newExpandedCards = new Set(expandedCards);
      if (isExpanded) {
        newExpandedCards.delete(id);
      } else {
        newExpandedCards.add(id);
      }
      setExpandedCards(newExpandedCards);
    };

    return (
      <Card className={`bg-gray-800 text-white ${className}`}>
        <CardHeader 
          className="p-4 lg:p-6 cursor-pointer"
          onClick={toggleCard}
        >
          <div className="flex justify-between items-center">
            <CardTitle className="text-lg">{title}</CardTitle>
            <div className="flex items-center gap-2">
              {filterComponent && isExpanded && (
                <div onClick={(e) => e.stopPropagation()} className="mr-2">
                  {filterComponent}
                </div>
              )}
              <ChevronDown 
                className={`w-5 h-5 transition-transform ${
                  isExpanded ? 'rotate-180' : ''
                }`}
              />
            </div>
          </div>
        </CardHeader>
        {isExpanded && (
          <CardContent className="p-4 lg:p-6">
            {children}
          </CardContent>
        )}
      </Card>
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <RefreshCw className="w-6 h-6 animate-spin text-blue-500" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 border border-red-500 bg-red-100 text-red-700 rounded-lg">
        <AlertCircle className="h-4 w-4 inline mr-2" />
        Error: {error}
      </div>
    );
  }

  // Calculate statistics
  const totalFeedback = analyzedFeedback.length;
  const sentimentStats = {
    positive: analyzedFeedback.filter(f => f.sentiment === 'positive').length,
    negative: analyzedFeedback.filter(f => f.sentiment === 'negative').length,
    neutral: analyzedFeedback.filter(f => f.sentiment === 'neutral').length
  };

  const categoryStats = analyzedFeedback.reduce((acc, feedback) => {
    acc[feedback.category] = (acc[feedback.category] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  const countryStats = analyzedFeedback.reduce((acc, feedback) => {
    const country = feedback.country || 'Unknown';
    acc[country] = (acc[country] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  const priorityStats = {
    high: analyzedFeedback.filter(f => f.priority === 'high').length,
    medium: analyzedFeedback.filter(f => f.priority === 'medium').length,
    low: analyzedFeedback.filter(f => f.priority === 'low').length
  };

  // Chart data
  const sentimentChartData = [
    { name: 'Positive', value: sentimentStats.positive, color: '#10B981' },
    { name: 'Negative', value: sentimentStats.negative, color: '#EF4444' },
    { name: 'Neutral', value: sentimentStats.neutral, color: '#6B7280' }
  ];

  const categoryChartData = Object.entries(categoryStats).map(([category, count]) => ({
    category: category.replace('_', ' ').toUpperCase(),
    count
  }));

  const topCountries = Object.entries(countryStats)
    .sort(([,a], [,b]) => b - a)
    .slice(0, 10)
    .map(([country, count]) => ({ country, count }));

  return (
    <div className="p-4 lg:p-8 bg-gray-900 min-h-screen">
      <div className="flex flex-col lg:flex-row lg:justify-between mb-6">
        <div className="flex items-center gap-4 mb-4 lg:mb-0">
          <a 
            href="/dashboard-process-logs" 
            className="inline-flex items-center gap-2 px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Back to Process Logs</span>
          </a>
          <h1 className="text-xl lg:text-3xl font-bold text-white">Feedback Analysis Dashboard</h1>
        </div>
        <div className="text-sm text-gray-400">
          Last updated: {lastUpdated.toLocaleTimeString()}
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <Card className="bg-gray-800 text-white">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-400">Total Feedback</p>
                <p className="text-2xl font-bold text-blue-400">{totalFeedback}</p>
              </div>
              <MessageSquare className="w-8 h-8 text-gray-500" />
            </div>
          </CardContent>
        </Card>

        

        <Card className="bg-gray-800 text-white">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-400">Positive</p>
                <p className="text-2xl font-bold text-green-400">{sentimentStats.positive}</p>
              </div>
              <ThumbsUp className="w-8 h-8 text-gray-500" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gray-800 text-white">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-400">Negative</p>
                <p className="text-2xl font-bold text-red-400">{sentimentStats.negative}</p>
              </div>
              <ThumbsDown className="w-8 h-8 text-gray-500" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gray-800 text-white">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-400">High Priority</p>
                <p className="text-2xl font-bold text-orange-400">{priorityStats.high}</p>
              </div>
              <Star className="w-8 h-8 text-gray-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      <ChartCard 
          title="Priority Feedback Queue" 
          id="feedback-list"
          filterComponent={desktopFilterComponent}
        >
          <div className="space-y-4">
            {/* Mobile Filters Section */}
            {isMobile && (
              <div className="grid grid-cols-1 gap-3 mb-4">
                <div className="grid grid-cols-2 gap-2">
                  <select 
                    value={filters.priority} 
                    onChange={(e) => {
                      e.stopPropagation();
                      setFilters({...filters, priority: e.target.value as any});
                    }}
                    className="bg-gray-700 text-white text-sm rounded-lg px-3 py-2 border border-gray-600"
                  >
                    <option value="all">All Priorities</option>
                    <option value="high">High Priority</option>
                    <option value="medium">Medium Priority</option>
                    <option value="low">Low Priority</option>
                  </select>
                  <select 
                    value={filters.sentiment} 
                    onChange={(e) => {
                      e.stopPropagation();
                      setFilters({...filters, sentiment: e.target.value as any});
                    }}
                    className="bg-gray-700 text-white text-sm rounded-lg px-3 py-2 border border-gray-600"
                  >
                    <option value="all">All Sentiments</option>
                    <option value="positive">Positive</option>
                    <option value="negative">Negative</option>
                    <option value="neutral">Neutral</option>
                  </select>
                </div>
                <select 
                  value={filters.category} 
                  onChange={(e) => {
                    e.stopPropagation();
                    setFilters({...filters, category: e.target.value as any});
                  }}
                  className="bg-gray-700 text-white text-sm rounded-lg px-3 py-2 border border-gray-600"
                >
                  <option value="all">All Categories</option>
                  <option value="bug">🐛 Bugs</option>
                  <option value="feature_request">✨ Feature Requests</option>
                  <option value="praise">👏 Praise</option>
                  <option value="complaint">😞 Complaints</option>
                  <option value="question">❓ Questions</option>
                  <option value="other">💬 Other</option>
                </select>
              </div>
            )}
            
            {/* Search Bar */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
              <input
                type="text"
                placeholder="Search feedback..."
                value={filters.search}
                onChange={handleSearchInputChange}
                className="w-full bg-gray-700 text-white rounded-lg pl-10 pr-4 py-3 border border-gray-600 focus:border-blue-500 focus:outline-none text-base"
              />
            </div>

            {/* Feedback List */}
            <div className="space-y-3">
              {filteredFeedback
                .slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)
                .map((feedback: FeedbackAnalysis) => {
                  const isExpanded = expandedFeedback.has(feedback.id);
                  
                  return (
                    <div 
                      key={feedback.id}
                      className="bg-gray-700/50 rounded-lg border border-gray-600 p-4 hover:bg-gray-700/70 transition-colors"
                    >
                      {/* Header Row */}
                      <div className={`flex items-start justify-between gap-4 ${isMobile ? 'flex-col' : ''}`}>
                        <div className={`flex items-center gap-3 flex-1 ${isMobile ? 'flex-wrap' : ''}`}>
                          {/* Priority Badge */}
                          <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getPriorityColor(feedback.priority)}`}>
                            {feedback.priority.toUpperCase()}
                          </span>
                          
                          {/* Sentiment Icon */}
                          {getSentimentIcon(feedback.sentiment)}
                          
                          {/* Category Badge */}
                          <span className={`${isMobile ? 'text-base' : 'text-sm'}`}>
                            {getCategoryIcon(feedback.category)} {feedback.category.replace('_', ' ')}
                          </span>
                          
                          {/* Confidence Score */}
                          {!isMobile && (
                            <span className="text-xs text-gray-400">
                              {Math.round(feedback.confidence * 100)}% confidence
                            </span>
                          )}
                        </div>
                        
                        {/* Expand/Collapse Button */}
                        <button
                          onClick={() => toggleFeedbackExpand(feedback.id)}
                          className={`text-gray-400 hover:text-white transition-colors ${isMobile ? 'self-end mt-2' : ''}`}
                        >
                          {isExpanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                        </button>
                      </div>

                      {/* Feedback Text */}
                      <div className="mt-4">
                        <p className={`text-gray-200 leading-relaxed ${isMobile ? 'text-base' : 'text-sm'}`}>
                          {isExpanded ? feedback.originalText : truncateText(feedback.originalText, isMobile ? 120 : 150)}
                        </p>
                      </div>

                      {/* Metadata Row */}
                      <div className={`mt-4 pt-3 border-t border-gray-600 ${isMobile ? 'space-y-2' : 'flex items-center justify-between'}`}>
                        <div className={`${isMobile ? 'grid grid-cols-2 gap-2' : 'flex items-center gap-4'} text-xs text-gray-400`}>
                          <div className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            <span className={isMobile ? 'text-xs' : 'text-xs'}>
                              {new Date(feedback.createdAt).toLocaleDateString()}
                            </span>
                          </div>
                          <div className="flex items-center gap-1">
                            <Globe className="w-3 h-3" />
                            <span>{feedback.country}</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <User className="w-3 h-3" />
                            <span>{feedback.isPaidUser ? 'Paid' : 'Free'}</span>
                          </div>
                          {isMobile && (
                            <div className="flex items-center gap-1 text-xs text-gray-400">
                              <span>{Math.round(feedback.confidence * 100)}% confidence</span>
                            </div>
                          )}
                          {!isMobile && (
                            <div>
                              Language: {feedback.language.toUpperCase()}
                            </div>
                          )}
                        </div>
                        
                        <div className={`text-xs text-gray-400 ${isMobile ? 'mt-2 text-center' : ''}`}>
                          ID: #{feedback.id}
                        </div>
                      </div>

                      {/* Expanded Details */}
                      {isExpanded && (
                        <div className="mt-4 pt-4 border-t border-gray-600 space-y-3">
                          {/* Themes */}
                          {feedback.themes.length > 0 && (
                            <div>
                              <h4 className="text-sm font-medium text-gray-300 mb-2">Detected Themes:</h4>
                              <div className="flex flex-wrap gap-2">
                                {feedback.themes.map((theme: string, index: number) => (
                                  <span 
                                    key={index}
                                    className="px-2 py-1 bg-blue-500/20 text-blue-400 rounded text-xs"
                                  >
                                    {theme}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                          
                          {/* AI Analysis Summary */}
                          <div className="bg-gray-800/50 rounded p-3">
                            <h4 className={`font-medium text-gray-300 mb-3 ${isMobile ? 'text-base' : 'text-sm'}`}>AI Analysis:</h4>
                            <div className={`${isMobile ? 'grid grid-cols-1 gap-3' : 'grid grid-cols-2 gap-4'} text-xs`}>
                              <div className={isMobile ? 'flex justify-between' : ''}>
                                <span className="text-gray-400">Sentiment:</span>
                                <span className="ml-2 text-white capitalize">{feedback.sentiment}</span>
                              </div>
                              <div className={isMobile ? 'flex justify-between' : ''}>
                                <span className="text-gray-400">Category:</span>
                                <span className="ml-2 text-white">{feedback.category.replace('_', ' ')}</span>
                              </div>
                              <div className={isMobile ? 'flex justify-between' : ''}>
                                <span className="text-gray-400">Priority:</span>
                                <span className="ml-2 text-white capitalize">{feedback.priority}</span>
                              </div>
                              <div className={isMobile ? 'flex justify-between' : ''}>
                                <span className="text-gray-400">Confidence:</span>
                                <span className="ml-2 text-white">{Math.round(feedback.confidence * 100)}%</span>
                              </div>
                              {isMobile && (
                                <div className="flex justify-between">
                                  <span className="text-gray-400">Language:</span>
                                  <span className="ml-2 text-white">{feedback.language.toUpperCase()}</span>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
            </div>

            {/* Pagination */}
            <div className={`pt-4 ${isMobile ? 'space-y-3' : 'flex justify-between items-center'}`}>
              <div className={`text-sm text-gray-400 ${isMobile ? 'text-center' : ''}`}>
                Showing {Math.min((currentPage - 1) * itemsPerPage + 1, filteredFeedback.length)} - {Math.min(currentPage * itemsPerPage, filteredFeedback.length)} of {filteredFeedback.length} feedback items
              </div>
              <div className={`flex gap-3 ${isMobile ? 'justify-center' : ''}`}>
                <button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className={`${isMobile ? 'px-4 py-2' : 'px-3 py-1'} bg-gray-600 text-white rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-500 transition-colors`}
                >
                  Previous
                </button>
                {isMobile && (
                  <span className="px-4 py-2 bg-gray-700 text-white rounded text-sm">
                    Page {currentPage}
                  </span>
                )}
                <button
                  onClick={() => setCurrentPage(currentPage + 1)}
                  disabled={currentPage * itemsPerPage >= filteredFeedback.length}
                  className={`${isMobile ? 'px-4 py-2' : 'px-3 py-1'} bg-gray-600 text-white rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-500 transition-colors`}
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        </ChartCard>

      {/* Charts */}
      <div className="flex flex-col space-y-4 pt-4">
        <ChartCard title="Sentiment Distribution" id="sentiment">
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={sentimentChartData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {sentimentChartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </ChartCard>

        <ChartCard title="Feedback Categories" id="categories">
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={categoryChartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis 
                  dataKey="category" 
                  stroke="#9CA3AF"
                  angle={-45}
                  textAnchor="end"
                  height={60}
                />
                <YAxis stroke="#9CA3AF" />
                <Tooltip 
                  contentStyle={{ backgroundColor: '#1F2937', border: 'none' }}
                />
                <Bar dataKey="count" fill="#3B82F6" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </ChartCard>

        <ChartCard title="Feedback by Country" id="geography">
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={topCountries}>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis dataKey="country" stroke="#9CA3AF" />
                <YAxis stroke="#9CA3AF" />
                <Tooltip 
                  contentStyle={{ backgroundColor: '#1F2937', border: 'none' }}
                />
                <Bar dataKey="count" fill="#10B981" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </ChartCard>


      </div>
    </div>
  );
};

export default FeedbackDashboard;